import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  SupersedeEvent,
  SupersedePreview,
  SupersedeScopeKind,
} from "@shared-core/data/cost-engine-validation-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";
import { Loader2, History } from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

/**
 * Settings › Catalogue › Live costing › Check settings → "Supersede checks".
 *
 * An engine build changes what "correct" means, and v1 stamps no engine
 * version onto a check, so the reset is explicit: pick a scope, a cut-off
 * and a reason, see how many checks that covers, confirm. Nothing is
 * deleted — superseded checks stop counting on every board and live-costing
 * check, and stay in the pair page's history with their reason.
 *
 * The preview is fetched before the confirm step (never after), because the
 * count is the only thing standing between "supersede this product type's
 * pre-fix batch" and "supersede the whole ledger".
 */

const SCOPE_LABELS: Record<SupersedeScopeKind, string> = {
  all: "Every check",
  product_type: "One product type",
  dictionary_item: "One dictionary item",
  cost_centre: "One cost centre",
};

const SCOPE_VALUE_LABELS: Partial<Record<SupersedeScopeKind, string>> = {
  product_type: "Product type ID",
  dictionary_item: "Dictionary item ID",
  cost_centre: "Cost centre ID",
};

function scopeSummary(event: {
  scopeKind: SupersedeScopeKind;
  scopeValue: string | null;
}): string {
  return event.scopeKind === "all"
    ? SCOPE_LABELS.all
    : `${SCOPE_LABELS[event.scopeKind]} · ${event.scopeValue}`;
}

/** `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` in local time;
 *  the API wants an ISO instant. These two convert between them. */
function toLocalInputValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function SupersedeChecksPanel() {
  const adapter = useDataAdapter();
  const [scopeKind, setScopeKind] = useState<SupersedeScopeKind>("all");
  const [scopeValue, setScopeValue] = useState("");
  const [checkedBefore, setCheckedBefore] = useState(() =>
    toLocalInputValue(currentTime()),
  );
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<SupersedePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [events, setEvents] = useState<SupersedeEvent[] | null>(null);
  const [eventsError, setEventsError] = useState(false);

  const loadEvents = useCallback(() => {
    setEventsError(false);
    adapter
      .listSupersedeEvents()
      .then(setEvents)
      .catch(() => setEventsError(true));
  }, [adapter]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const request = useMemo(
    () => ({
      scopeKind,
      scopeValue: scopeKind === "all" ? null : scopeValue.trim(),
      checkedBefore: new Date(checkedBefore).toISOString(),
      reason,
    }),
    [scopeKind, scopeValue, checkedBefore, reason],
  );

  const canPreview =
    reason.trim().length > 0 &&
    (scopeKind === "all" || scopeValue.trim().length > 0);

  // Any edit invalidates a preview: confirming against a stale count is the
  // one mistake this panel exists to prevent.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
  }, [scopeKind, scopeValue, checkedBefore, reason]);

  async function runPreview() {
    if (!canPreview) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await adapter.previewSupersedeChecks(request));
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      setPreviewError(
        fieldErrors.length > 0
          ? fieldErrors.map((e) => e.message).join("; ")
          : err instanceof Error
            ? err.message
            : "Couldn’t work out how many checks that covers.",
      );
    } finally {
      setPreviewing(false);
    }
  }

  async function submitSupersede() {
    setSubmitting(true);
    try {
      const result = await adapter.supersedeChecks(request);
      toast.success(
        `Superseded ${result.event.checksSuperseded} check${
          result.event.checksSuperseded === 1 ? "" : "s"
        } on ${result.event.itemsAffected} item${
          result.event.itemsAffected === 1 ? "" : "s"
        }`,
      );
      setConfirming(false);
      setPreview(null);
      setReason("");
      setScopeValue("");
      loadEvents();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      toast.error(
        fieldErrors.length > 0
          ? fieldErrors.map((e) => e.message).join("; ")
          : err instanceof Error
            ? err.message
            : "Failed to supersede checks",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <History className="size-4" /> Supersede checks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          When an engine build changes what “correct” means, the checks written
          before it are comparing the old engine with Tharstern — and the
          four-week window keeps counting them. Superseding withdraws them: they
          stop counting on every board and every live-costing check, and stop
          counting as “this press has been used in a price check”. Nothing is
          deleted — each superseded check stays in the pair page’s history,
          greyed, with the reason you give here.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Scope</span>
            <Select
              value={scopeKind}
              onValueChange={(value) => {
                setScopeKind(value as SupersedeScopeKind);
                setScopeValue("");
              }}
            >
              <SelectTrigger aria-label="Scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SCOPE_LABELS) as SupersedeScopeKind[]).map(
                  (kind) => (
                    <SelectItem key={kind} value={kind}>
                      {SCOPE_LABELS[kind]}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </label>

          {scopeKind !== "all" && (
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">
                {SCOPE_VALUE_LABELS[scopeKind]}
              </span>
              <Input
                value={scopeValue}
                onChange={(e) => setScopeValue(e.target.value)}
                placeholder={
                  scopeKind === "dictionary_item" ? "dict-…" : "e.g. 33"
                }
              />
            </label>
          )}

          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">
              Supersede checks made before
            </span>
            <Input
              type="datetime-local"
              value={checkedBefore}
              onChange={(e) => setCheckedBefore(e.target.value)}
            />
          </label>
        </div>

        <label className="space-y-1 text-xs block">
          <span className="text-muted-foreground">
            Reason (required — recorded on every check this supersedes)
          </span>
          <Textarea
            placeholder="e.g. engine build 6a491e243 deployed"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!canPreview || previewing}
            onClick={() => void runPreview()}
          >
            {previewing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "Preview"
            )}
          </Button>
          {preview && (
            <p className="text-sm">
              This will supersede{" "}
              <span className="font-medium">{preview.checkCount}</span> check
              {preview.checkCount === 1 ? "" : "s"} on{" "}
              <span className="font-medium">{preview.itemCount}</span> item
              {preview.itemCount === 1 ? "" : "s"}.
            </p>
          )}
          {previewError && (
            <p role="alert" className="text-sm text-destructive">
              {previewError}
            </p>
          )}
          {preview && preview.checkCount > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirming(true)}
            >
              Supersede
            </Button>
          )}
        </div>

        <div className="space-y-2 border-t border-dashed border-border pt-3">
          <p className="text-xs font-medium text-foreground">
            Previously superseded
          </p>
          {eventsError ? (
            <p className="text-xs text-destructive">
              Couldn’t load the supersede history. Please try again.
            </p>
          ) : events === null ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No checks have been superseded yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {events.map((event) => (
                <li key={event.id} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {scopeSummary(event)}
                  </span>{" "}
                  · {event.checksSuperseded} check
                  {event.checksSuperseded === 1 ? "" : "s"} on{" "}
                  {event.itemsAffected} item
                  {event.itemsAffected === 1 ? "" : "s"} · before{" "}
                  {formatDateTimeOrDash(event.checkedBefore)} ·{" "}
                  {event.supersededBy},{" "}
                  {formatDateTimeOrDash(event.supersededAt)} “{event.reason}”
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supersede these checks?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview?.checkCount} check
              {preview?.checkCount === 1 ? "" : "s"} on {preview?.itemCount}{" "}
              item{preview?.itemCount === 1 ? "" : "s"} —{" "}
              {scopeSummary({
                scopeKind,
                scopeValue: scopeKind === "all" ? null : scopeValue.trim(),
              })}
              , made before {formatDateTimeOrDash(request.checkedBefore)}. Their
              items’ live-costing checks are re-evaluated straight away, so a
              Live item resting on superseded evidence may be paused. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting}
              onClick={(e) => {
                e.preventDefault();
                void submitSupersede();
              }}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Supersede"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
